<template>
<div>
  <div v-if="!loadingFeed && feedDetail !== null">
    <!-- Intro -->
    <div class="mb-lg">
      <p class="has-text-weight-bold has-text-info has-text-centered mb-md">Step 2 of 3: Review Import</p>
      <p class="has-text-grey">
        Please review the results of your import and make sure they meet
        your expectations. Please remember that SKUs cannot be on another active proposal -- otherwise, the item will be skipped.
      </p>
    </div>
    <div class="mb-lg">
      <div class="has-background-light rounded-lg pa-md">
        <Progress :feed="feedDetail" size="is-large" :emitRefresh="true" @refresh="loadFeed(true)" />
      </div>
    </div>
    <!-- Proposal Details -->
    <div v-if="!loadingProposal && proposalDetail !== null && feedDetail.status === 'success'">
      <!-- Item Counters -->
      <div class="mb-lg">
        <!-- Items -->
        <div class="mb-sm">
          <div class="columns">
            <div class="column is-half">
              <div class="has-background-light rounded-lg pa-md has-text-centered">
                <p class="is-size-4 has-text-weight-bold">
                  <span v-if="activeProposalItems > 0" class="has-text-success">{{ activeProposalItems }}</span>
                  <span v-else class="has-text-grey-light">0</span>
                </p>
                <p class="has-text-grey is-size-7"># Items Added to Proposal</p>
              </div>
            </div>
            <div class="column is-half">
              <div class="has-background-light rounded-lg pa-md has-text-centered">
                <p class="is-size-4 has-text-weight-bold">
                  <span v-if="skippedItems > 0" class="has-text-warning">{{ skippedItems }}</span>
                  <span v-else class="has-text-success">0</span>
                </p>
                <p class="has-text-grey is-size-7"># Items Skipped from Proposal</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Warning -->
      <div class="mt-lg" v-if="skippedItems > 0">
        <div v-if="activeProposalItems <= 0 && skippedItems > 0">
          <b-message class="is-size-7" type="is-warning">
            Items are skipped from your proposal if they do not exist, <span v-if="proposalDetail.proposal_type === 'new_product'">have previously been approved,</span> or are already on another
            proposal awaiting approval from {{ proposalDetail.connection.retailer.name }}. Unfortunately, all of the items you submitted
            met one or more of these conditions. Please archive this proposal.
          </b-message>
        </div>
        <div v-else>
          <b-message class="is-size-7" type="is-warning">
            Items are skipped for your proposal if they do not exist, <span v-if="proposalDetail.proposal_type === 'new_product'">have previously been approved,</span> or are already on another
            proposal awaiting approval from {{ proposalDetail.connection.retailer.name }}.
          </b-message>
        </div>
      </div>
      <!-- Show details -->
      <div class="mt-sm has-text-right">
        <a href="#" class="has-text-info is-size-7" @click="showDetails = !showDetails" v-text="(showDetails) ? 'Hide Details' : 'Show Details'"></a>
      </div>
      <!-- Feed Details -->
      <div class="mb-lg">
        <div v-if="showDetails" class="mt-md">
          <ImportSummary :feedDetail={...feedDetail} />
        </div>
      </div>
      <!-- Navigate -->
      <div class="mt-xl">
        <div class="columns is-centered">
          <div class="column">
            <b-button
              type="is-light rounded-lg"
              size="is-medium"
              @click="goBack"
              expanded
              ><b-icon icon="arrow-left"></b-icon>
            </b-button>
          </div>
          <div v-if="activeProposalItems > 0" class="column">
            <b-button
              type="is-primary rounded-lg"
              size="is-medium"
              expanded
              :disabled="saving"
              :loading="saving"
              @click="goForward"
            >Review Compatibility</b-button>
          </div>
          <div v-else-if="activeProposalItems <= 0 && proposalDetail.item_rejected_count > 0" class="column">
            <b-button
              type="is-primary rounded-lg"
              size="is-medium"
              expanded
              :disabled="saving"
              :loading="saving"
              @click="archiveProposal"
            >Archive Proposal</b-button>
          </div>
          <div v-else class="column">
            <b-button
              type="is-primary rounded-lg"
              size="is-medium"
              expanded
              :disabled="true"
            >Review Compatibility</b-button>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div v-else>
    <loading message="Waiting on import details..." />
  </div>
</div>
</template>

<script>
import { mapGetters } from 'vuex'
import Progress from '@/components/Models/FeedQueue/Presenters/Progress'
import ImportSummary from '@/components/Models/FeedQueue/Presenters/ImportSummary'

export default {
  components: {
    Progress,
    ImportSummary
  },
  computed: {
    ...mapGetters(['app', 'context']),
    skippedItems () {
      return this.proposalDetail.item_rejected_count + this.proposalDetail.item_skipped_count
    },
    activeProposalItems () {
      if (this.proposalDetail) {
        return this.proposalDetail.item_count - this.proposalDetail.item_rejected_count
      }
      return 0
    },
    statusPresenter () {
      const status = this.feedDetail.processed_at !== null && this.$utils.find(this.feedDetail.jobs[0].metadata, ['name', 'status'])
      switch (status.value) {
        case 'Successful':
          return { class: 'has-text-success', label: status.value }
        default:
          return { class: 'has-text-warning', label: status.value }
      }
    }
  },
  data () {
    return {
      loadingFeed: true,
      loadingProposal: true,
      loadingCompatibility: true,
      feedDetail: null,
      proposalDetail: null,
      rulesets: [],
      showDetails: false,
      saving: false,
      errors: {}
    }
  },
  methods: {
    goBack () {
      this.$emit('navigate', 'Build')
    },
    goForward () {
      this.$emit('navigate', 'Compatibility')
    },
    toggleDetails () {
      this.showDetails = !this.showDetails
    },
    loadProposal (silent = false) {
      if (!silent) this.loadingProposal = true
      const endpoint = this.context.route + '/proposals/' + this.proposal.id + '/'
      this.$http.get(endpoint).then(response => {
        this.proposalDetail = response.data
        this.loadingProposal = false
      })
    },
    loadFeed (silent = false) {
      if (!silent) this.loadingFeed = true
      const endpoint = this.context.route + '/feed-queue/' + this.feed.id + '/'
      this.$http.get(endpoint).then(response => {
        this.feedDetail = response.data
        this.$emit('feedDetails:queued', response.data)
        if (this.feedDetail.status === 'success' || this.feedDetail.status === 'failure') {
          this.loadProposal(true)
        }
        this.loadingFeed = false
      })
    },
    submitProposal () {
      this.saving = true
      const endpoint = this.context.route + '/proposals/' + this.proposal.id + '/'
      const body = {
        status: 'proposed'
      }
      this.$http.patch(endpoint, body).then(response => {
        this.$buefy.toast.open({ type: 'is-success', message: 'Proposal submitted!' })
        this.$router.push({ name: 'catalog' })
      })
    },
    archiveProposal () {
      this.saving = true
      const endpoint = this.context.route + '/proposals/' + this.proposal.id + '/'
      const body = {
        status: 'archived'
      }
      this.$http.patch(endpoint, body).then(response => {
        this.$buefy.toast.open({ type: 'is-info', message: 'Proposal archived!' })
        this.$router.push({ name: 'catalog' })
      })
    },
    getJobMetadataFromKey (row, key, onlyValue) {
      let job = null
      if (row.length && row[0].metadata.length) {
        job = row[0].metadata.find(el => el.name === key)
      }
      if (job && job.value && onlyValue) {
        return job.value
      } else {
        return job !== null && job !== undefined
      }
    }
  },
  mounted () {
    this.loadFeed()
  },
  props: {
    feed: {
      type: Object,
      required: true
    },
    proposal: {
      type: Object,
      required: true
    }
  }
}
</script>
